import React from 'react';

interface RatingDisplayProps {
  score: number; // 0-100
  size?: 'sm' | 'md' | 'lg';
  showBreakdown?: boolean;
  breakdown?: {
    experiencia: number;
    cursos: number;
    opiniones: number;
    participacion: number;
    perfil: number;
  };
  className?: string;
}

interface RatingWeights {
  experiencia: number;
  cursos: number;
  opiniones: number;
  participacion: number;
  perfil: number;
}

const DEFAULT_WEIGHTS: RatingWeights = {
  experiencia: 0.2,
  cursos: 0.2,
  opiniones: 0.25,
  participacion: 0.2,
  perfil: 0.15
};

export default function RatingDisplay({ 
  score, 
  size = 'md', 
  showBreakdown = false,
  breakdown,
  className = '' 
}: RatingDisplayProps) {

  const getRatingLevel = (score: number): { level: string; color: string } => {
    if (score >= 80) return { level: 'Excelente', color: 'success' };
    if (score >= 65) return { level: 'Bueno', color: 'primary' };
    if (score >= 45) return { level: 'Regular', color: 'warning' };
    return { level: 'Bajo', color: 'danger' };
  };

  const getStarRating = (score: number): number => {
    return Math.round((score / 100) * 5 * 10) / 10; // Redondeo a 1 decimal
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return { text: 'fs-6', badge: 'px-2 py-1', progress: 'progress-sm' };
      case 'lg': return { text: 'fs-4', badge: 'px-3 py-2', progress: 'progress-lg' };
      default: return { text: 'fs-5', badge: 'px-3 py-2', progress: '' };
    }
  };

  const { level, color } = getRatingLevel(score);
  const starRating = getStarRating(score);
  const sizeClasses = getSizeClasses();

  const getProgressBarColor = (value: number) => {
    if (value >= 80) return 'bg-success';
    if (value >= 65) return 'bg-primary';
    if (value >= 45) return 'bg-warning';
    return 'bg-danger';
  };

  return (
    <div className={`rating-display ${className}`}>
      {/* Calificación Principal */}
      <div className="d-flex align-items-center mb-2">
        <div className={`text-${color} fw-bold ${sizeClasses.text} me-3`}>
          {score}/100
        </div>
        <span className={`badge text-bg-${color} ${sizeClasses.badge} me-3`}>
          {level}
        </span>
        <div className="d-flex align-items-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <i
              key={star}
              className={`fas fa-star me-1 ${
                star <= Math.floor(starRating) 
                  ? 'text-warning' 
                  : star === Math.ceil(starRating) && starRating % 1 !== 0
                  ? 'text-warning opacity-50'
                  : 'text-muted'
              }`}
            />
          ))}
          <span className="text-muted ms-2">({starRating}/5)</span>
        </div>
      </div>

      {/* Barra de Progreso General */}
      <div className={`progress mb-3 ${sizeClasses.progress}`} style={{ height: size === 'lg' ? '12px' : '8px' }}>
        <div 
          className={`progress-bar ${getProgressBarColor(score)}`}
          role="progressbar" 
          style={{ width: `${score}%` }}
          aria-valuenow={score}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Desglose Detallado */}
      {showBreakdown && breakdown && (
        <div className="rating-breakdown">
          <h6 className="text-muted mb-3">Desglose por Criterio</h6>
          
          <div className="row">
            <div className="col-md-6">
              {/* Años de Experiencia */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    <i className="fas fa-user-graduate me-1"></i>
                    Años de Experiencia
                  </small>
                  <small className="fw-bold">{breakdown.experiencia}%</small>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${getProgressBarColor(breakdown.experiencia)}`}
                    style={{ width: `${breakdown.experiencia}%` }}
                  />
                </div>
                <small className="text-muted">
                  Peso: {Math.round(DEFAULT_WEIGHTS.experiencia * 100)}%
                </small>
              </div>

              {/* Cursos y Talleres */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    <i className="fas fa-graduation-cap me-1"></i>
                    Cursos y Talleres
                  </small>
                  <small className="fw-bold">{breakdown.cursos}%</small>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${getProgressBarColor(breakdown.cursos)}`}
                    style={{ width: `${breakdown.cursos}%` }}
                  />
                </div>
                <small className="text-muted">
                  Peso: {Math.round(DEFAULT_WEIGHTS.cursos * 100)}%
                </small>
              </div>

              {/* Perfil Completo */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    <i className="fas fa-id-card me-1"></i>
                    Perfil Completo
                  </small>
                  <small className="fw-bold">{breakdown.perfil}%</small>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${getProgressBarColor(breakdown.perfil)}`}
                    style={{ width: `${breakdown.perfil}%` }}
                  />
                </div>
                <small className="text-muted">
                  Peso: {Math.round(DEFAULT_WEIGHTS.perfil * 100)}%
                </small>
              </div>
            </div>

            <div className="col-md-6">
              {/* Opiniones */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    <i className="fas fa-star me-1"></i>
                    Opiniones de Clientes
                  </small>
                  <small className="fw-bold">{breakdown.opiniones}%</small>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${getProgressBarColor(breakdown.opiniones)}`}
                    style={{ width: `${breakdown.opiniones}%` }}
                  />
                </div>
                <small className="text-muted">
                  Peso: {Math.round(DEFAULT_WEIGHTS.opiniones * 100)}%
                </small>
              </div>

              {/* Participación Activa */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    <i className="fas fa-chart-line me-1"></i>
                    Participación Activa
                  </small>
                  <small className="fw-bold">{breakdown.participacion}%</small>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${getProgressBarColor(breakdown.participacion)}`}
                    style={{ width: `${breakdown.participacion}%` }}
                  />
                </div>
                <small className="text-muted">
                  Peso: {Math.round(DEFAULT_WEIGHTS.participacion * 100)}%
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Hook personalizado para calcular calificaciones
export const useRatingCalculation = () => {
  const calculateRating = (data: {
    anosExperiencia: number;
    cursosCompletados: number;
    promedioEstrellas: number;
    numPropiedades: number;
    perfilCompleto: boolean;
  }, weights: RatingWeights = DEFAULT_WEIGHTS) => {
    
    // 1. Años de experiencia (0-100)
    let expScore = 0;
    if (data.anosExperiencia >= 10) expScore = 100;
    else if (data.anosExperiencia >= 6) expScore = 75;
    else if (data.anosExperiencia >= 3) expScore = 50;
    else if (data.anosExperiencia > 0) expScore = 25;

    // 2. Cursos completados (0-100)
    let cursoScore = 0;
    if (data.cursosCompletados >= 11) cursoScore = 100;
    else if (data.cursosCompletados >= 7) cursoScore = 75;
    else if (data.cursosCompletados >= 4) cursoScore = 50;
    else if (data.cursosCompletados > 0) cursoScore = 25;

    // 3. Opiniones - promedio de estrellas convertido a porcentaje (0-100)
    const opinionScore = (data.promedioEstrellas / 5) * 100;

    // 4. Participación - basada en número de propiedades (0-100)
    let partScore = 0;
    if (data.numPropiedades >= 10) partScore = 100;
    else if (data.numPropiedades >= 5) partScore = 50;
    else if (data.numPropiedades > 0) partScore = 25;

    // 5. Perfil completo - binario (0 o 100)
    const perfilScore = data.perfilCompleto ? 100 : 0;

    // Cálculo final con pesos
    const calificacionFinal = Math.round(
      expScore * weights.experiencia +
      cursoScore * weights.cursos +
      opinionScore * weights.opiniones +
      partScore * weights.participacion +
      perfilScore * weights.perfil
    );

    return {
      calificacionFinal,
      breakdown: {
        experiencia: expScore,
        cursos: cursoScore,
        opiniones: Math.round(opinionScore),
        participacion: partScore,
        perfil: perfilScore
      }
    };
  };

  return { calculateRating };
};